import React, {useEffect, useRef, useState} from 'react';
import PropertyGroup from '../properties/PropertyGroup';
import Panel from '../layout/Panel';
import Button from '../layout/Button';
import {TrashIcon} from '../Iconset';
import {CustomEventTypes, TrackerTypes} from '../../globals';
import Engine from '../../classes/Engine';
import ObjectPropertiesComponent from '../objectInspector/ObjectPropertiesComponent';
import TransformComponent from '../objectInspector/TransformComponent';
import TrackerComponent from '../objectInspector/TrackerComponent';
import RemoveObjectAction from '../../actions/RemoveObjectAction';
import SelectObjectsAction from '../../actions/SelectObjectsAction';
import AddARTrackerAction from '../../actions/AddARTrackerAction';
import Scenario from '../../classes/scenarioElements/Scenario';
import ARTracker from '../../classes/ARTracker';
import useForceUpdate from '../reactUtils/useForceUpdate';
import useEventListener from '../reactUtils/useEventListener';
import useCurrentStep from '../reactUtils/useCurrentStep';
import { KeyCodes } from '../../interfaces/Input';

export default function UITrackersEditor()
{
	const forceUpdate = useForceUpdate();

	useEventListener(CustomEventTypes.onTrackerListUpdated, handleTrackerlistUpdate, document);
	useEventListener(CustomEventTypes.objectNameChanged, () => forceUpdate(), document);
	useEventListener(CustomEventTypes.objectSelectionChanged, handleTrackerlistUpdate, document);

	//global ui store
	const currentStep = useCurrentStep();
	const currentSequence = Scenario.currentSequence();

	const [trackerList, setTrackerList] = useState<Array<ARTracker>>([]);
	const [shouldScroll, setShouldScroll] = useState(false);

	const uiEditorRef = useRef<HTMLDivElement>(null);

	useEffect(() =>
	{
		handleTrackerlistUpdate();
	}, [currentStep, currentSequence]);

	useEffect(() =>
	{
		if (shouldScroll)
		{
			scrollDown();
		}
	}, [shouldScroll]);

	function handleTrackerlistUpdate()
	{
		if (!currentStep.id) return;
		if (trackerList?.length < ARTracker.getTrackerList().length) setShouldScroll(true);
		setTrackerList(ARTracker.getTrackerList());
		forceUpdate();
	}

	function deleteTracker(tracker: ARTracker)
	{
		new RemoveObjectAction([tracker]);
	}

	function addTracker()
	{
		new AddARTrackerAction(currentStep);
		setShouldScroll(true);
	}

	function trackerNameDisplay(tracker: ARTracker)
	{
		let name = tracker.name;
		if (name.length > 20) name = name.substring(0, 20) + "..."
		switch (tracker.trackerType)
		{
			case TrackerTypes.SLAM:
				return name + " (Surface)";
			case TrackerTypes.MODEL:
				return name + " (Object)";
			case TrackerTypes.IMAGE:
				return name + " (Image)";
		}
	}

	function scrollDown()
	{
		if (uiEditorRef && uiEditorRef.current)
		{
			uiEditorRef.current.scrollTop = uiEditorRef.current.scrollHeight;
		}
		setShouldScroll(false);
	}

	function handleKeyDown(e: React.KeyboardEvent)
	{
		if (e.keyCode === KeyCodes.DELETE)
		{
			const selectedTrackers = new Array<ARTracker>();
			let trackersCount = 0;
			if(trackerList.length <= 1) return;

			for (let i = 0; i < trackerList.length; i++)
			{
				const tracker = trackerList[i];
				if (Engine.getInstance().getPickingManager().isObjectSelected(tracker))
				{
					trackersCount++;
					selectedTrackers.push(tracker);
				}
			}

			if(trackersCount === trackerList.length) return;
			new RemoveObjectAction(selectedTrackers);
		}
	}

	const renderTrackers = (() =>
	{
		if (!trackerList || trackerList.length <= 0) return;
		const trackersToDraw = new Array<JSX.Element>();

		let tempKey = 0;
		for (const tracker of trackerList)
		{
			trackersToDraw.push(
				<PropertyGroup
					title={trackerNameDisplay(tracker) || "Tracker"}
					key={tracker.userData.guid}
					decorators={trackerList.length > 1 ? <TrashIcon scale={0.75} onClick={() => { deleteTracker(tracker) }} /> : null}
					onClick={() => 
					{ 
						if (!Engine.getInstance().getPickingManager().isObjectSelected(tracker))
						{
							new SelectObjectsAction([tracker], Engine.getInstance().getPickingManager().getCurrentSelection().objects);
						}
					}}
					drawSelected={Engine.getInstance().getPickingManager().isObjectSelected(tracker)}
				>
					<div style={{ marginLeft: '4px' }}>
						<ObjectPropertiesComponent noGroup={true} key={tempKey} objects={[tracker]} nameCharacterLimit={60} />
						<TransformComponent noGroup={true} objects={[tracker]} />
						<TrackerComponent noGroup={true} objects={[tracker]} />
					</div>
				</PropertyGroup>
			)
			tempKey++;
		}
		return trackersToDraw;
	})();

	return (
	//Trackers UI Editor
		<Panel data-name={"ui-editor-trackers-root"}
			onKeyDownCallback={handleKeyDown}>
			<Panel data-name={"ui-editor-trackers"} 
				ref={uiEditorRef} 
				style={{ position: "relative"}}
			>
				{renderTrackers}
			</Panel>
			<div className={"listWindowAddItemButtonWrapper"}>
				<Button
					label={"Add AR Tracker"}
					category={"Project"}
					data-name={"trackers-add-new"}
					key={"trackersAddNewTracker"}
					onClick={addTracker}
				/>
			</div>
		</Panel>
	)
}